import { DateTime } from 'luxon';
import { err, ok, Result } from 'neverthrow';
import { groupBy } from 'lodash';
import { Database } from './Supabase';
import { SupabaseQuotaInfo } from './QuotaInfo';
import { useToday } from '@/composables/useToday';

export interface QuotaMeta {
	quotaInfoId: string;
	monthlyHours: number;
	description?: string;
	isExtra: boolean;
	from: DateTime;
	till?: DateTime;
	userId: string;
}

export interface Quota extends QuotaMeta {
	quotaId: string;
}

export type QuotaSubmitData = Pick<QuotaMeta, 'description' | 'from' | 'till' | 'isExtra' | 'monthlyHours'> & Partial<Pick<QuotaMeta, 'quotaInfoId'>> & { quotaId?: string };
export type QuotaValidationOptions = {
	activeQuotaDuringTimeRange?: Quota | null;
	allowOverwrite?: boolean;
	isProject?: boolean;
	status?: 'update' | 'create';
};

export type SupabaseQuota = Database['public']['Tables']['quotas']['Row'];

export const createQuotaFromSupabase = (quota: SupabaseQuota, quotaInfo: SupabaseQuotaInfo): Quota => ({
	quotaId: quota.quota_id,
	from: quota.from ? DateTime.fromISO(quota.from) : DateTime.fromISO(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`),
	till: quota.till ? DateTime.fromISO(quota.till) : undefined,
	isExtra: quotaInfo.is_extra ?? false,
	monthlyHours: quota.monthly_hours,
	description: quota.description ?? undefined,
	userId: quota.user_id,
	quotaInfoId: quota.quota_info_id,
});

export const toSupabaseData = (quotaMeta: QuotaMeta): Omit<SupabaseQuota, 'quota_id'> => ({
	quota_info_id: quotaMeta.quotaInfoId,
	description: quotaMeta.description ?? null,
	monthly_hours: quotaMeta.monthlyHours,
	from: quotaMeta.from.toISODate() ?? '',
	till: quotaMeta.till?.toISODate() ?? null,
	user_id: quotaMeta.userId,
});

export const startsAfterSpecificDate = (date: DateTime, quota: Quota) => {
	const startOfDate = date.startOf('day');
	const quotaStart = quota.from.startOf('day');

	return startOfDate < quotaStart;
};

export const startsBeforeSpecificDate = (date: DateTime, quota: Quota) => {
	const startOfDate = date.startOf('day');
	const quotaStart = quota.from.startOf('day');

	return startOfDate > quotaStart;
};

export const startsOnSameDate = (date: DateTime, quota: Quota) => {
	const startOfDate = date.startOf('day');
	const quotaStart = quota.from.startOf('day');

	return startOfDate.equals(quotaStart);
};

export const endsOnSameDate = (date: DateTime, quota: Quota) => {
	const startOfDate = date.startOf('day');
	const quotaEnd = quota.till?.startOf('day');

	if (!quotaEnd) {
		return false;
	}

	return startOfDate.equals(quotaEnd);
};

export const isActiveDuringSpecificDate = (date: DateTime, quota: Quota) => {
	const startOfDate = date.startOf('day');
	const quotaEnd = quota.till?.startOf('day');

	if (startsOnSameDate(date, quota) || endsOnSameDate(date, quota)) {
		return true;
	}

	if (!startsBeforeSpecificDate(date, quota)) {
		return false;
	}

	return quotaEnd ? quotaEnd > startOfDate : true;
};

export const isActiveDuringSpecificDateRange = (startDate: DateTime, endDate: DateTime, quota: Quota) => {
	const startOfDate = startDate.startOf('day');
	const endOfDate = endDate.startOf('day');

	return isActiveDuringSpecificDate(startOfDate, quota) || isActiveDuringSpecificDate(endOfDate, quota);
};

export const getRemainingQuotaHoursForSpecificDate = (clientQuotas: Quota[], projectQuotas: Quota[], date: DateTime): number | null => {
	const activeClientQuota = clientQuotas.find((quota) => isActiveDuringSpecificDate(date, quota));

	if (!activeClientQuota) {
		return null;
	}

	const activeProjectQuotas = projectQuotas.filter((quota) => isActiveDuringSpecificDate(date, quota));

	const totalActiveProjectQuotaHours = activeProjectQuotas.reduce((prev, curr) => prev + curr.monthlyHours, 0);

	return activeClientQuota.monthlyHours - totalActiveProjectQuotaHours;
};

export const getActiveQuotaDuringTimeRange = (
	range: { from: DateTime; till?: DateTime },
	quotas: Quota[],
): Quota | null => (quotas
	.find((q) => (range.till
		? isActiveDuringSpecificDateRange(range.from, range.till, q)
		: isActiveDuringSpecificDate(range.from, q))))
	?? null;

export const removeQuotaFromArray = (quota: Quota, quotas: Quota[]): Quota[] => quotas.filter((q) => q.quotaId !== quota.quotaId);

export type QuotaValidationErrorMessage = 'projectIsInTimeRange' | 'activeQuotaDuringTimeRange' | 'insufficientQuotaCapacity' | 'quotaExceedsNewQuotaLimit' | 'quotaExceedsNewClientQuotaLimit' | 'startsAfterSpecificDate' | 'noAllowOverwrite' | 'showOverwriteQuotaConfirmationModal';

export type QuotaValdationError = {
	message: QuotaValidationErrorMessage;
	exceededHours?: number;
};
export const getActiveQuotasInTimeRange = (
	range: { from: DateTime; till?: DateTime },
	quotas: Quota[],
): Quota[] =>
	quotas.filter((currentQuota) =>
		currentQuota.from <= (range.till || useToday().value)
		&& (!currentQuota.till || currentQuota.till >= range.from) && !currentQuota.isExtra,
	);

const getHighestValue = (array: number[]): number =>
	array.toSorted((a, b) => b - a)[0];

/**
 * Calculates the maximum possible sum of monthly hours from overlapping quotas within a given time range.
 *
 * The function:
 * 1. Filters quotas that are active within the specified time range
 * 2. For each active quota, finds all overlapping quotas in its period
 * 3. Groups overlapping quotas by their quotaInfoId
 * 4. For each group, takes the highest monthlyHours value
 * 5. Sums these highest values from all groups
 * 6. Returns the maximum sum found across all active quotas
 */
export const getMaxHoursInTimeRange = (range: { from: DateTime; till?: DateTime }, quotas: Quota[]): number => {
	const activeQuotas = getActiveQuotasInTimeRange(range, quotas);

	const maxMonthlyHoursDuringQuota = activeQuotas.map((quota) => {
		const overlappingQuotas = getActiveQuotasInTimeRange({ from: quota.from, till: quota.till }, activeQuotas);
		const overlappingQuotasGroupedById = groupBy(overlappingQuotas, 'quotaInfoId');
		const highestPossibleValuePerGroup = Object
			.values(overlappingQuotasGroupedById)
			.map((groupQuotas) => groupQuotas.map((q) => q.monthlyHours))
			.map(getHighestValue);
		return highestPossibleValuePerGroup.reduce((previous, current) => previous + current, 0);
	});

	const maxMonthlyHours = getHighestValue(maxMonthlyHoursDuringQuota);

	return maxMonthlyHours;
};

export const validateQuota
	= ({
		newQuota,
		clientQuotas,
		projectQuotas,
	}: {
		newQuota: QuotaSubmitData;
		clientQuotas: Quota[];
		projectQuotas?: Quota[];
	},
	options: QuotaValidationOptions): Result<boolean, QuotaValdationError> => {
		const sortedClientQuotas = clientQuotas?.toSorted((a, b) => ((b.till?.toMillis() ?? Infinity) - (a.till?.toMillis() ?? Infinity)));
		const lastClientQuota = sortedClientQuotas[0];

		if ((!lastClientQuota && options.isProject) || (lastClientQuota?.till && lastClientQuota?.till < newQuota.from)) {
			return ok(true);
		}

		if (projectQuotas && !newQuota.isExtra) {
			const sortedProjectQuota = projectQuotas.filter((projectQuota) =>
				projectQuota.till && projectQuota.quotaInfoId === newQuota.quotaInfoId && !projectQuota.isExtra)
				.toSorted((a, b) => ((b.till?.toMillis() ?? Infinity) - (a.till?.toMillis() ?? Infinity)));

			if (sortedProjectQuota[1]?.from > newQuota.from && sortedProjectQuota[1]?.till && sortedProjectQuota[1].till > newQuota.from) {
				return err({ message: 'startsAfterSpecificDate' });
			}

			const editQuotas = projectQuotas.map((projectQuota) => {
				if ('quotaId' in newQuota && projectQuota.quotaId === newQuota.quotaId) {
					return { ...projectQuota, from: newQuota.from, till: newQuota.till };
				}
				return projectQuota;
			});

			if (!('quotaId' in newQuota) && newQuota?.quotaInfoId && projectQuotas.findIndex((q) => q.quotaInfoId === newQuota.quotaInfoId) !== -1) {
				editQuotas.push({ ...newQuota, quotaId: '0', userId: '0', quotaInfoId: newQuota.quotaInfoId });
			}

			const maxHours = getMaxHoursInTimeRange({ from: newQuota.from, till: newQuota.till }, editQuotas);
			const activeClientHours = lastClientQuota?.monthlyHours || 0;

			if (!options.isProject) {
				if (lastClientQuota?.till && lastClientQuota.till > newQuota.from && options.status !== 'update') {
					return err({ message: 'startsAfterSpecificDate' });
				}

				if (newQuota.from > lastClientQuota?.from
					&& projectQuotas.find((projectQuota) =>
						projectQuota.from < newQuota.from && projectQuota.from > lastClientQuota.from)) {
					return err({ message: 'projectIsInTimeRange' });
				}
			}

			if (!options.isProject && maxHours > newQuota.monthlyHours) {
				return err({ message: 'quotaExceedsNewClientQuotaLimit', exceededHours: maxHours });
			} else if (options.isProject) {
				const activeProjectHours
					= projectQuotas
						.filter((projectQuota) => projectQuota.quotaInfoId === newQuota.quotaInfoId)
						.toSorted((a, b) => ((b.till?.toMillis() ?? Infinity) - (a.till?.toMillis() ?? Infinity)))[0]?.monthlyHours || 0;

				const maxHoursWithoutActiveProject = maxHours - activeProjectHours;

				if ((maxHoursWithoutActiveProject + newQuota.monthlyHours) > activeClientHours) {
					return err({ message: 'insufficientQuotaCapacity', exceededHours: activeClientHours - maxHoursWithoutActiveProject });
				}
			}
		}

		if (options.activeQuotaDuringTimeRange) {
			if (options.activeQuotaDuringTimeRange.till) {
				return err({ message: 'activeQuotaDuringTimeRange' });
			}

			if (startsAfterSpecificDate(newQuota.from, options.activeQuotaDuringTimeRange) && newQuota.till && options.activeQuotaDuringTimeRange.from < newQuota.till) {
				return err({ message: 'startsAfterSpecificDate' });
			}

			if (!options.allowOverwrite) {
				return err({ message: 'noAllowOverwrite' });
			}

			return err({ message: 'showOverwriteQuotaConfirmationModal' });
		}

		return ok(true);
	};
